import type { ArtifactListingEntry, PluginFolderState } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

export function listAppArtifacts(conversation?: string): Promise<ArtifactListingEntry[]> {
  const query =
    conversation === undefined ? '' : `?conversation=${encodeURIComponent(conversation)}`;
  return apiFetch(`/api/artifacts/index${query}`, { cache: 'no-store' });
}

export function artifactVersions(
  conversation: string,
  path: string,
): Promise<{ revision: number; createdAt: string; source: string }[]> {
  return apiFetch(
    `/api/artifacts/versions?conversation=${encodeURIComponent(conversation)}&path=${encodeURIComponent(path)}`,
    { cache: 'no-store' },
  );
}

export async function publishArtifactVersion(
  conversation: string,
  path: string,
  revision: number,
): Promise<void> {
  await apiFetch('/api/artifacts/publish', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ conversation, path, revision }),
  });
}

/** Tags (or untags) an artifact `template`, in the workspace of the conversation that lists it. */
export async function tagArtifactTemplate(
  artifact: Pick<ArtifactListingEntry, 'space' | 'path' | 'conversation'>,
  tagged: boolean,
): Promise<void> {
  await apiFetch('/api/artifacts/tags', {
    method: tagged ? 'PUT' : 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      ...(artifact.conversation === null ? {} : { conversation: artifact.conversation }),
      space: artifact.space,
      path: artifact.path,
      tag: 'template',
    }),
  });
}

export type ArtifactContent = {
  path: string;
  content: string;
  encoding: 'utf8' | 'base64';
  reported?: boolean;
  /** What a save names to be refused if the file changed since (absent: what an execution reported). */
  version?: string;
};

/**
 * Saves a file a common viewer edits (a Markdown plan, a task ticked) where it is: refused if it
 * changed since `version`. Answers its new version.
 */
export function saveArtifactContent(input: {
  space: 'workspace' | 'drive';
  path: string;
  plugin: string;
  conversation?: string | null;
  content: string;
  version?: string | undefined;
}): Promise<{ version: string }> {
  return apiFetch('/api/artifacts/content', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      space: input.space,
      path: input.path,
      plugin: input.plugin,
      ...(input.conversation ? { conversation: input.conversation } : {}),
      content: input.content,
      ...(input.version === undefined ? {} : { version: input.version }),
    }),
  });
}

export function artifactContent(input: {
  space: 'workspace' | 'drive' | 'plugin';
  path: string;
  plugin: string;
  conversation?: string | null;
}): Promise<ArtifactContent> {
  const query = new URLSearchParams({
    space: input.space,
    path: input.path,
    plugin: input.plugin,
  });
  if (input.conversation) query.set('conversation', input.conversation);
  return apiFetch(`/api/artifacts/content?${query}`, { cache: 'no-store' });
}

/**
 * Installs (or updates) a plugin folder a conversation listed, with what the user approved;
 * without `grants`, what installing it would ask (built afresh).
 */
export function installPluginFolder(
  conversation: string,
  path: string,
  grants?: readonly string[],
): Promise<PluginFolderState> {
  return apiFetch('/api/artifacts/plugin', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ conversation, path, grants }),
  });
}
